/**
 * UTM Parameters - Cookie-based storage
 * Saves UTM tags to cookies, reads from cookies when filling forms.
 * Same logic as theme forms, but uses cookies instead of localStorage.
 *
 * @package DesignSchool\Forms
 */

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
/** click ids from ads (not utm_*) - same cookie blob as UTM for first-hit capture before URL is cleaned */
const AD_CLICK_KEYS = ['ysclid', 'yclid'];
const COOKIE_NAME = 'utm_params';
const COOKIE_DAYS = 365;

function setCookie(name: string, value: string, days: number = COOKIE_DAYS): void {
    let expires = '';
    if (days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + encodeURIComponent(value || '') + expires + '; path=/';
}

function getCookie(name: string): string | null {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) {
            const val = c.substring(nameEQ.length, c.length);
            try {
                return decodeURIComponent(val);
            } catch {
                return val;
            }
        }
    }
    return null;
}

function getAttributionParamsFromURL(): Record<string, string> | null {
    const params = new URLSearchParams(window.location.search);
    const data: Record<string, string> = {};
    let hasAny = false;
    UTM_KEYS.forEach((key) => {
        const val = params.get(key);
        if (val) {
            data[key] = val;
            hasAny = true;
        }
    });
    AD_CLICK_KEYS.forEach((key) => {
        const val = params.get(key);
        if (val) {
            data[key] = val;
            hasAny = true;
        }
    });
    return hasAny ? data : null;
}

function loadUTM(): Record<string, string> {
    try {
        const raw = getCookie(COOKIE_NAME);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

function saveUTM(data: Record<string, string>): void {
    setCookie(COOKIE_NAME, JSON.stringify(data), COOKIE_DAYS);
}

function getReferrerUrl(): string | null {
    const rawReferrer = (document.referrer || '').trim();
    if (!rawReferrer) {
        return null;
    }

    try {
        const referrerUrl = new URL(rawReferrer, window.location.href);
        const currentUrl = new URL(window.location.href);

        // do not persist self-referrer
        if (referrerUrl.href === currentUrl.href) {
            return null;
        }

        // Browser/headers can reduce same-origin referrer to origin only.
        // In this case keep the full current page URL as the best available source.
        const isOriginOnlySameOrigin =
            referrerUrl.origin === currentUrl.origin &&
            referrerUrl.pathname === '/' &&
            referrerUrl.search === '' &&
            referrerUrl.hash === '';
        if (isOriginOnlySameOrigin) {
            return currentUrl.href;
        }

        return referrerUrl.href;
    } catch {
        return rawReferrer;
    }
}

/**
 * Merge current URL attribution into cookie.
 * If the URL carries any utm_* (or ysclid/yclid), drop the whole stored group first so old values are not mixed with the new link.
 */
export function refreshAttributionFromUrl(): void {
    const currentParams = getAttributionParamsFromURL();
    if (!currentParams) {
        return;
    }
    let currentUtm = loadUTM();
    const urlHasUtm = UTM_KEYS.some((k) => !!currentParams[k]);
    const urlHasAd = AD_CLICK_KEYS.some((k) => !!currentParams[k]);
    if (urlHasUtm) {
        UTM_KEYS.forEach((k) => {
            delete currentUtm[k];
        });
    }
    if (urlHasAd) {
        AD_CLICK_KEYS.forEach((k) => {
            delete currentUtm[k];
        });
    }
    Object.keys(currentParams).forEach((key) => {
        currentUtm[key] = currentParams[key];
    });
    saveUTM(currentUtm);
}

/**
 * Initialize UTM: read from URL, save to cookies; update referrer
 */
function initUTM(): void {
    let currentUtm = loadUTM();

    const referrer = getReferrerUrl();
    if (referrer && currentUtm.referrer !== referrer) {
        currentUtm.referrer = referrer;
        saveUTM(currentUtm);
    }

    refreshAttributionFromUrl();
}

/**
 * Apply UTM to form inputs in the given context
 */
function applyUTM(context: Document | Element = document): void {
    const utmData = loadUTM();
    const root = context instanceof Document ? context : context.ownerDocument || document;

    const fillKeys = UTM_KEYS.concat(AD_CLICK_KEYS);
    fillKeys.forEach((key) => {
        (context as Element).querySelectorAll?.('input[name="' + key + '"]').forEach((input: Element) => {
            const el = input as HTMLInputElement;
            el.value = utmData[key] ? utmData[key] : '';
        });
    });

    (context as Element).querySelectorAll?.('input[name="referrer"]').forEach((input: Element) => {
        const el = input as HTMLInputElement;
        el.value = utmData.referrer ? utmData.referrer : '';
    });
}

/**
 * Get UTM data from cookies (for manual use, e.g. append to FormData)
 */
export function getUtmFromCookies(): Record<string, string> {
    return loadUTM();
}

// Run init on load (before DOM ready, to capture referrer)
initUTM();

// Expose for theme compatibility and plugin use
if (typeof window !== 'undefined') {
    (window as any).forceUTM = applyUTM;
    (window as any).dsFormsUtmLoad = loadUTM;
    (window as any).dsFormsUtmSave = saveUTM;
    (window as any).dsFormsUtmRefresh = refreshAttributionFromUrl;
    window.addEventListener('pageshow', () => {
        refreshAttributionFromUrl();
    });
}

export { applyUTM, loadUTM, saveUTM, UTM_KEYS, AD_CLICK_KEYS };
